import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { useVisibilityObserver } from '../../../helpers/hooks/useVisibilityObserver';
import AnalyticsWidgetLoader from '../analyticsWidgetLoader';
import WidgetSkeletonPlaceholder from '../../widgetSkeletonPlaceholder';

interface LazyAnalyticsWidgetLoaderProps {
  dashboard: string;
  dashboardWidget: string;
  widget: {
    url: string;
    type: string;
    render_type?: string;
    [key: string]: any;
  };
}

/**
 * Lazy loading wrapper for AnalyticsWidgetLoader.
 *
 * For ClickHouse sessions (enable_clickhouse: true), this component defers
 * loading of the widget until it becomes visible in the viewport. This reduces
 * the number of concurrent ClickHouse queries when a page has many widgets.
 *
 * For non-ClickHouse sessions, the widget loads immediately (same as before).
 *
 * Uses IntersectionObserver with a 200px root margin to pre-load widgets
 * slightly before they enter the viewport for a smoother experience.
 *
 * Also defers re-fetching (e.g., on filter changes) for widgets that are
 * currently not visible, loading them when they become visible again.
 */
const LazyAnalyticsWidgetLoader = ({
  dashboard,
  dashboardWidget,
  widget,
}: LazyAnalyticsWidgetLoaderProps) => {
  const { company_details } = useSelector((state: RootState) => state.company);

  // Enable lazy loading only for ClickHouse sessions
  const enableLazyLoad = (company_details as any)?.enable_clickhouse === true;

  const { ref, hasBeenVisible, isCurrentlyVisible } = useVisibilityObserver({
    enabled: enableLazyLoad,
    // Pre-load when widget is 200px from entering viewport
    rootMargin: '200px 0px 200px 0px',
  });

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      {hasBeenVisible ? (
        <AnalyticsWidgetLoader
          dashboard={dashboard}
          dashboardWidget={dashboardWidget}
          widget={widget}
          isCurrentlyVisible={isCurrentlyVisible}
          lazyLoadEnabled={enableLazyLoad}
        />
      ) : (
        <WidgetSkeletonPlaceholder />
      )}
    </div>
  );
};

export default LazyAnalyticsWidgetLoader;
